/* ============================================================================
   Zoonicorn — base behaviours: scroll reveal + cloud drift.

   No framework, no dependencies. Both features degrade to "content is simply
   visible and still", which is the correct failure mode for decoration.
   ========================================================================= */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  /* --------------------------------------------------------------------------
     Scroll reveal.

     The .zoo-js class is what activates the hidden start state in CSS. It is
     set here rather than in the HTML so that if this script never runs — parse
     error, blocked asset, old browser — nothing is ever hidden.
     ----------------------------------------------------------------------- */
  /** @type {IntersectionObserver | null} */
  let revealObserver = null;

  const collect = (root) => {
    const found = Array.from(root.querySelectorAll('.zoo-reveal'));
    // querySelectorAll only looks at descendants, so a section element that is
    // itself a reveal target would be missed when re-scanning one section.
    if (root instanceof Element && root.classList.contains('zoo-reveal')) found.unshift(root);
    return found;
  };

  const initReveal = (root = document) => {
    const targets = collect(root);
    if (!targets.length) return;

    if (reduced.matches || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    document.documentElement.classList.add('zoo-js');

    root.querySelectorAll('.zoo-reveal-group').forEach((group) => {
      Array.from(group.children).forEach((child, i) => {
        child.style.setProperty('--zoo-i', String(i));
      });
    });

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            // One-shot: re-animating on every scroll-by is noise, not motion.
            revealObserver.unobserve(entry.target);
            entry.target.addEventListener(
              'transitionend',
              () => entry.target.style.removeProperty('will-change'),
              { once: true }
            );
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
      );
    }

    targets.forEach((el) => revealObserver.observe(el));
  };

  /* --------------------------------------------------------------------------
     Cloud drift.

     Reads scroll position once per frame and writes a single custom property.
     Never reads layout inside the rAF callback — that is what turns a parallax
     into jank. Elements are measured on load, on resize, and when the theme
     editor changes the page — never on a scroll tick.
     ----------------------------------------------------------------------- */
  let boxes = [];
  let ticking = false;
  let cloudsWired = false;

  // Horizon's base.css puts `overflow: hidden; height: 100dvh` on <html> above
  // 990px and scrolls .page-wrapper instead. On that layout window.scrollY is
  // permanently 0 and `scroll` never fires on window, so a parallax written
  // against window silently computes one value at load and then never moves
  // again -- which is exactly what ours was doing on every desktop. Below 990px
  // the document scrolls normally, and the breakpoint can be crossed by a
  // resize, so the container is resolved on each pass rather than cached.
  const getScroller = () => {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1) return wrapper;
    return document.scrollingElement || document.documentElement;
  };

  const measure = () => {
    const scroller = getScroller();
    const top = scroller.scrollTop;
    // A scrolled .page-wrapper starts at the top of the viewport, but not
    // necessarily at y=0 -- an announcement bar above it shifts the whole
    // container -- so its own offset comes out of the element's position.
    const originY = scroller === (document.scrollingElement || document.documentElement)
      ? 0
      : scroller.getBoundingClientRect().top;
    // Re-queried rather than cached: the theme editor adds and removes
    // sections, so a stored NodeList would keep measuring elements that are no
    // longer in the document.
    boxes = Array.from(document.querySelectorAll('.zoo-clouds')).map((el) => {
      const r = el.getBoundingClientRect();
      return { el, top: r.top - originY + top, height: r.height, width: r.width };
    });
    phase();
    watchForReflow();
  };

  /* Everything below exists because measure() used to run ONCE, at
     DOMContentLoaded, and a stored top that is wrong is a phase that is wrong,
     which is a visible horizontal seam in the sky.

     /pages/characters was the proof. Three .zoo-clouds layers, all 1920 wide,
     all sharing a 8815.5px tile. .zoo-cast at page-top 1080 phased to -960 and
     .zoo-news at 2174.8 phased to -2054.8, both correct. The watch band sat at
     2779 and should have phased to -2659; it read -2863.95, which is the phase
     for a top of 2983.95. Nothing was wrong with the modulo -- the number it
     was fed was 205px stale, measured before a webfont swapped in and reflowed
     the copy above it.

     So: re-measure after the load event, after the fonts settle, and whenever
     any cloud section's own box changes size.

     This cannot feed back on itself. measure() writes exactly one custom
     property, --zoo-cloud-phase, which is read only by background-position. It
     changes no element's box, so nothing it does can retrigger the observer
     that called it. New sections are observed once each -- re-observing an
     element already in a ResizeObserver fires its initial callback again, and
     THAT would loop -- so the WeakSet is load-bearing, not decoration. */
  let cloudRO = null;
  const observedClouds = new WeakSet();

  const watchForReflow = () => {
    if (!cloudRO) return;
    document.querySelectorAll('.zoo-clouds').forEach((el) => {
      if (observedClouds.has(el)) return;
      observedClouds.add(el);
      cloudRO.observe(el);
    });
  };

  let lateWired = false;
  const wireLateMeasure = () => {
    if (lateWired) return;
    lateWired = true;

    // Images below the fold finish after DOMContentLoaded, and each one that
    // resolves at a different intrinsic height moves every section under it.
    if (document.readyState === 'complete') {
      requestAnimationFrame(measure);
    } else {
      addEventListener('load', measure, { once: true });
    }

    // The brand faces are woff2 with a fallback stack behind them; the swap
    // reflows every line of copy on the page.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }

    if (typeof ResizeObserver === 'function') {
      let pending = false;
      cloudRO = new ResizeObserver(() => {
        if (pending) return;
        // Coalesced to one re-measure per frame: a font swap resizes several
        // sections at once and each would otherwise cost its own layout read.
        pending = true;
        requestAnimationFrame(() => {
          pending = false;
          measure();
        });
      });
      watchForReflow();
    }
  };

  // The cloud field is ONE continuous sky, but it is painted by a separate
  // pseudo-element per section, each of which repeated the tile from its own
  // top edge (background-position: 50% 0%). With sections 130, 1080, 130, 672,
  // 820 and 548 tall, the pattern restarted six times down the About page and
  // every restart sliced a cloud in half on a hard horizontal line.
  //
  // The fix is to phase each section's tile by where that section actually sits
  // in the page, so the repeat is continuous across the seams. The tile is
  // drawn at `background-size: 100% auto`, so its height is the element's width
  // times the artwork's own aspect ratio -- read off the image itself rather
  // than hardcoded, because the cloud asset is a theme setting.
  //
  // That aspect ratio is only the design's cycle because zoo-clouds.webp is
  // exactly 1920x1002: the artwork is 1914 wide and the boards draw it flush
  // left with 6px of bare sky down the right-hand edge, so the asset carries
  // those 6 transparent columns instead of being stretched to fill them. Drawn
  // at 1914/1920 the cycle came out 1005.14px tall and walked 3px further down
  // the page every repeat.
  let tileRatio = 0;
  const bleed = () =>
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--zoo-cloud-bleed')) || 0;

  // Where the sky BEGINS, measured down from the top of the first blue section
  // on the page. Not a phase: a start.
  //
  // The XD's cloud layer is not an infinite wallpaper. Every artboard carries a
  // `Clouds` group holding four placed PNGs -- 800x373 at x0, 800x257 at x1114,
  // 800x257 at x40, 800x293 at x981 -- stacked on a 1002px cycle and repeated a
  // FINITE number of times, with clean sky above the first of them. Repeating
  // the tile upwards into that band paints a cloud the boards do not have: a
  // 1002px cycle has only 20 empty rows in it, so no choice of phase can leave
  // the band empty -- the field has to actually start. --zoo-cloud-gap is how
  // much of the first section's layer is cut away to make that happen, and the
  // clip-path in zoonicorn-base.css is what applies it.
  //
  // The distance itself is measured off the rendered boards, not read out of
  // the graphics tree: correlating zoo-clouds.webp against each board at its
  // native 1002px cycle puts the first tile row at y1306 on Characters, Home,
  // About, Watch and Activities, whose sky all starts at y1080 -- 226px. (The
  // AGC frames say 248. They are 22px out on every board; the board wins.)
  //
  // Anchored to the first cloud section rather than to the page, because the
  // hero above it is a merchant-set image whose height is not ours to assume.
  const BOARD_WIDTH = 1920;
  const originPx = () =>
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--zoo-cloud-origin')) || 0;

  const phase = () => {
    if (!tileRatio || !boxes.length) return;
    const over = bleed();
    const origin = originPx();   // theme-wide fallback
    // The first section in DOM order is the top of the field. querySelectorAll
    // returns document order, and measure() keeps that order.
    const first = boxes[0];
    // ...and it is also the only section allowed to say where the field starts.
    // The boards do not agree on one number: Characters, Home, About, Watch and
    // Activities all begin 226px below their sky, the Movie board 313, the store
    // index 123 and a character page 82. A theme setting holds one value, so the
    // page that disagrees carries its own on its first cloud section -- see
    // snippets/zoonicorn-cloud-start.liquid. Blank there means no attribute,
    // which is what lets the theme setting stay the fallback.
    const own = parseFloat(first.el.dataset.cloudStart);
    const start = first.top + (Number.isNaN(own) ? origin : own)
      * ((first.width || BOARD_WIDTH) / BOARD_WIDTH);
    boxes.forEach(({ el, top, width }) => {
      const tileH = width * tileRatio;
      if (!tileH) return;
      // ::before starts --zoo-cloud-bleed above the section, so that is where
      // the repeat has to be anchored -- not at the section's own top edge.
      const bleedTop = top - over;
      el.style.setProperty('--zoo-cloud-phase', `${-((((bleedTop - start) % tileH) + tileH) % tileH)}px`);
      // The supplied cloud repeat pattern tiles smoothly across all sections
      el.style.setProperty('--zoo-cloud-gap', '0px');
    });
  };

  const loadTileRatio = () => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--zoo-cloud-image');
    const url = (raw.match(/url\(["']?([^"')]+)["']?\)/) || [])[1];
    if (!url) return;
    const img = new Image();
    img.onload = () => {
      if (!img.naturalWidth) return;
      tileRatio = img.naturalHeight / img.naturalWidth;
      phase();
    };
    img.src = url;
  };

  const initClouds = () => {
    if (!document.querySelector('.zoo-clouds')) return;

    // Phasing is LAYOUT, not motion, and has to happen unconditionally.
    //
    // It is the only thing making the per-section cloud layers read as one
    // sky. It used to sit behind the parallax guard below, so with the
    // parallax slider at 0 -- or for any visitor with prefers-reduced-motion
    // set, which includes most headless renderers -- measure() never ran, no
    // --zoo-cloud-phase was written, and every section fell back to
    // `background-position: center 0`. Each one then restarted the 1914x8788
    // tile at its own top edge, so the same cloud arrangement painted again a
    // few hundred pixels down the page. That repeat is the "pattern appearing
    // twice" on the activity pages: two sections, one tile, two starts.
    loadTileRatio();
    measure();
    wireLateMeasure();

    const strength = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--zoo-cloud-parallax')
    );

    if (!strength || reduced.matches) {
      // No drift, but the field still has to re-phase when the page reflows.
      document.documentElement.style.setProperty('--zoo-cloud-shift', '0');
      if (cloudsWired) return;
      cloudsWired = true;
      addEventListener('resize', measure, { passive: true });
      return;
    }

    const update = () => {
      ticking = false;
      const scroller = getScroller();
      const scroll = scroller.scrollTop;
      // The container's own height, not innerHeight: when .page-wrapper is the
      // scroller these are the same, but reading it off the element keeps the
      // maths right if Horizon ever insets it.
      const viewport = scroller.clientHeight;
      // ONE shift for the whole page, not one per section. Section-relative
      // progress moved neighbouring sections by different amounts, which tore
      // the sky apart at every boundary however well the tiles were phased.
      // A single document-wide value keeps the field rigid as it drifts.
      const travel = Math.max(1, scroller.scrollHeight - viewport);
      const shift = Math.round((scroll / travel - 0.5) * 120 * strength);
      document.documentElement.style.setProperty('--zoo-cloud-shift', String(shift));
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update();

    // Wired once. initClouds runs again on every section load, and re-adding
    // these each time would leave a scroll handler per section the merchant
    // has ever touched.
    if (cloudsWired) return;
    cloudsWired = true;
    // Both targets: the document scrolls below 990px, .page-wrapper above it,
    // and a resize can move the page from one to the other without a reload.
    addEventListener('scroll', onScroll, { passive: true, capture: true });
    addEventListener('resize', () => { measure(); update(); }, { passive: true });
  };

  const start = () => {
    initReveal();
    initClouds();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* --------------------------------------------------------------------------
     Theme editor.

     Without this, a section added or moved in the editor keeps the hidden
     start state that .zoo-js applies and never reveals — the merchant drops in
     a section and sees nothing. Reveal is re-scanned for the new subtree only;
     the cloud layers are re-measured because the page's geometry just changed.
     ----------------------------------------------------------------------- */
  document.addEventListener('shopify:section:load', (event) => {
    initReveal(event.target);
    initClouds();
  });

  document.addEventListener('shopify:section:unload', () => {
    // The removed elements are gone from the observer with them; all that is
    // left to correct is where everything below them now sits.
    measure();
  });
})();
